import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Shield, Plus, X, Sparkles, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';

interface Skill {
  id: string;
  skill_name: string;
  proficiency_level: string | null;
  source_type?: string | null;
}

interface Props {
  skills: Skill[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  editLocked?: boolean;
  onEditingChange?: (active: boolean) => void;
}

const MAX_ITEMS = 12;

export function SkillsSection({ skills, profileId, onAdd, onDelete, editLocked, onEditingChange }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Skill | null>(null);

  const atMax = skills.length >= MAX_ITEMS;

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.sectionId === 'section-skills' && !atMax && !editLocked) {
        setEditing(true);
        onEditingChange?.(true);
      }
    };
    window.addEventListener('open-section-edit', handler);
    return () => window.removeEventListener('open-section-edit', handler);
  }, [atMax, editLocked, onEditingChange]);

  const handleAdd = () => {
    if (!name.trim() || atMax) return;
    onAdd({ skill_name: name.trim(), candidate_profile_id: profileId, source_type: 'skill' });
    setName('');
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <Card className="overflow-hidden bg-white border-border">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <div className="w-8 h-8 rounded-lg bg-seek/10 flex items-center justify-center">
              <Shield className="w-4.5 h-4.5 text-seek" />
            </div>
            {t('seek.profile.skillsExpertise')}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2.5 py-0.5">
              {skills.length}/{MAX_ITEMS}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!editing && editLocked) return;
                const next = !editing;
                setEditing(next);
                onEditingChange?.(next);
              }}
              disabled={!editing && editLocked}
              className={editing
                ? "gap-1 bg-seek text-white hover:bg-seek/90"
                : "gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white"
              }
            >
              <Pencil className="w-4 h-4" /> {editing ? t('seek.profile.done') : t('seek.profile.edit')}
            </Button>
          </div>
        </div>

        {editing && !atMax && (
          <div className="rounded-xl border border-seek/20 bg-seek/5 p-4 mb-5">
            <p className="text-xs font-semibold text-seek uppercase tracking-wide mb-3">{t('seek.profile.addNewSkill')}</p>
            <div className="flex gap-2 items-center">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('seek.profile.skillName')}
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                autoFocus
              />
              <Button size="sm" onClick={handleAdd} disabled={!name.trim()} className="bg-seek text-white hover:bg-seek/90 shrink-0">
                <Plus className="w-4 h-4 me-1" /> {t('seek.profile.add')}
              </Button>
            </div>
          </div>
        )}

        {skills.length === 0 && !editing ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-seek/10 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-seek" />
            </div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{t('seek.profile.noSkills')}</p>
            <p className="text-xs text-muted-foreground/60">{t('seek.profile.skillsHelp')}</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex flex-wrap justify-center gap-2">
              {skills.map((s) => (
                <Badge
                  key={s.id}
                  variant="secondary"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-xl border border-seek/15 bg-seek/8 text-seek shadow-sm transition-all hover:bg-seek/15 hover:shadow-md"
                >
                  <span dir={detectTextDirection(s.skill_name)}>{s.skill_name}</span>
                  {editing && (
                    <button
                      onClick={() => setDeleteTarget(s)}
                      className="ms-0.5 text-destructive/60 hover:text-destructive transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('seek.profile.removeSkillTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('seek.profile.removeSkillDesc', { name: deleteTarget?.skill_name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t('common.remove')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
